import type { Expr } from '../core/types';

/**
 * OpenLayers builds a GLSL float literal by appending ".0" when a number's string form
 * has no decimal point. JavaScript uses exponential notation below 1e-6 and at or above
 * 1e21, so such a value compiles to `1e-7.0` and the entire fragment shader fails.
 *
 * This bit once, silently: a black map and a stack trace with no mention of the number.
 * Every literal reaching an expression goes through here.
 */
export const GLSL_SAFE = (n: number): boolean =>
  Number.isFinite(n) && !/e/i.test(String(n));

/** The value if emittable, otherwise a stated fallback. */
export const SN = (n: number, fallback = 0): number => (GLSL_SAFE(n) ? n : fallback);

/** The value if emittable, otherwise null so the caller can restructure instead. */
export const lit = (n: number): number | null => (GLSL_SAFE(n) ? n : null);

export const ADD = (a: Expr, b: Expr): Expr => ['+', a, b];
export const SUB = (a: Expr, b: Expr): Expr => ['-', a, b];
export const MUL = (a: Expr, b: Expr): Expr => ['*', a, b];

/** Never divide by ~0. 1e-6 is the smallest epsilon with a plain decimal form. */
export const SAFE = (d: Expr): Expr => ['case', ['<', ['abs', d], 1e-6], 1e-6, d];
export const DIV = (n: Expr, d: Expr): Expr => ['/', n, SAFE(d)];
export const ND = (a: Expr, b: Expr): Expr => DIV(SUB(a, b), ADD(a, b));

/** Walk an expression tree and collect every numeric literal. */
export function literals(e: Expr): number[] {
  const out: number[] = [];
  (function walk(n: Expr) {
    if (typeof n === 'number') { out.push(n); return; }
    if (Array.isArray(n)) n.slice(1).forEach(walk);
  })(e);
  return out;
}

/** Any literal that would not compile. Empty means the tree is emittable. */
export const unsafeLiterals = (e: Expr): number[] => literals(e).filter((n) => !GLSL_SAFE(n));
